import { useState, useMemo, useCallback, useEffect } from 'react';
import { useDropzone, type Accept } from 'react-dropzone';
import { ChevronLeft, ChevronRight, ChevronDown, FileText, Download, FileCheck, BookOpen, Trash2, Sparkles, AlertCircle, Loader2, Check, X, RotateCcw, UploadCloud } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import type { CaseDocument, DocumentStatus } from '../../types';
import { DOCUMENT_STATUS_CONFIG } from '../../types';
import { cn } from '../ui/utils';
import { IconTooltip } from '../ui/tooltip';
import { formatDate, formatFileSize } from '../../utils/formatters';

// Drop-zone upload constraints — kept in sync with the create-case wizard
// (CaseWizardModal) so files dropped onto the sidebar are accepted on the same
// terms as files added through the wizard.
const UPLOAD_MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024; // 2GB
const UPLOAD_ACCEPTED_FILE_TYPES: Accept = {
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/msword': ['.doc'],
  'text/csv': ['.csv'],
  'message/rfc822': ['.eml'],
  'application/vnd.ms-outlook': ['.msg'],
  'application/vnd.ms-outlook-pst': ['.pst'],
  'application/zip': ['.zip'],
  'application/x-zip-compressed': ['.zip'],
};

/**
 * Extract file type from filename.
 */
function getFileType(name: string): string {
  const ext = name.split('.').pop()?.toUpperCase() || 'FILE';
  return ext;
}

export interface EntityProgressInfo {
  detected: number;
  approved: number;
  rejected: number;
}

/** Format a seconds estimate as a short human label, e.g. "~1m 20s left". */
function formatEta(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `~${s}s left`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem === 0 ? `~${m}m left` : `~${m}m ${rem}s left`;
}

/**
 * "Analyzing..." indicator with progress bar and a live time estimate.
 *
 * The server reports progress + an ETA on each per-page tick (polled every few
 * seconds); between polls we count the ETA down locally once a second so it
 * feels live rather than jumping in 3s steps. Each fresh `etaSeconds` from the
 * server resets the local countdown.
 */
function AnalyzingIndicator({ progress, etaSeconds }: { progress: number; etaSeconds?: number }) {
  const [displayEta, setDisplayEta] = useState<number | undefined>(etaSeconds);

  useEffect(() => {
    setDisplayEta(etaSeconds);
    if (etaSeconds == null) return;
    const interval = setInterval(() => {
      setDisplayEta((prev) => (prev == null ? prev : Math.max(0, prev - 1)));
    }, 1000);
    return () => clearInterval(interval);
  }, [etaSeconds]);

  return (
    <div className="mt-2">
      <div className="flex items-center justify-between text-[11px] text-[#7035C4] mb-1">
        <span className="flex items-center gap-1">
          <Sparkles className="h-3 w-3 animate-pulse" />
          Analyzing…
        </span>
        {displayEta != null && <span className="tabular-nums">{formatEta(displayEta)}</span>}
      </div>
      <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
        {progress > 0 ? (
          // Real progress reported by the worker: fill the bar proportionally.
          <div
            className="h-full bg-[#7035C4] rounded-full transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        ) : (
          // Queued / not started yet: indeterminate sweep so it isn't stuck empty.
          <div className="h-full bg-[#7035C4] rounded-full animate-indeterminate-progress" />
        )}
      </div>
    </div>
  );
}

/**
 * Single bulk-redaction rescan indicator for a document row. One small dot:
 * spinning while the rescan is in flight, then a green tick (done) or red X
 * (failed) that fades out. It renders inline on the right of the label row that
 * sits directly above the entity progress bar (in place of the % figure while
 * rescanning), so it never changes the card's size — the bar stays put and
 * refreshes in place once the rescan lands. Initial scans keep the detailed
 * `AnalyzingIndicator` instead.
 */
function RescanDot({ phase }: { phase: RescanDotPhase }) {
  const fading = phase !== 'spin';
  return (
    <span
      className={`inline-flex items-center ${
        phase === 'x' ? 'text-red-600' : 'text-[#7035C4]'
      } ${fading ? 'animate-fade-out' : ''}`}
      title={phase === 'spin' ? 'Rescanning…' : phase === 'check' ? 'Rescan done' : 'Rescan failed'}
    >
      {phase === 'spin' && <Loader2 className="h-3 w-3 animate-spin" />}
      {phase === 'check' && <Check className="h-3 w-3" />}
      {phase === 'x' && <X className="h-3 w-3" />}
    </span>
  );
}

interface DocumentsPanelProps {
  documents: CaseDocument[];
  selectedDocumentId: string | null;
  onSelectDocument: (documentId: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onDownload: (documentId: string, version: 'original' | 'redacted') => void;
  onOpenFileDict?: (documentId: string) => void;
  onDeleteDocument?: (documentId: string) => void;
  onReprocessDocument?: (documentId: string) => void;
  /** Re-queue a plain processing job for a failed document. Distinct from
   *  onReprocessDocument (which steers detection with manual flags). */
  onRetryProcessing?: (documentId: string) => void;
  entityProgress?: Map<string, EntityProgressInfo>;
  processingDocumentIds?: Set<string>;
  processingProgress?: Map<string, number>;
  processingEta?: Map<string, number>;
  failedDocumentIds?: Set<string>;
  /** Failure reason per failed document, keyed by document id. Shown next to the
   *  "Processing failed" state so the reviewer knows why it failed. */
  failureReasons?: Map<string, string>;
  /** Per-document bulk-redaction rescan indicator: a single dot that spins while
   *  a rescan is in flight, then briefly shows a tick (done) or X (failed). */
  rescanDotStatus?: Map<string, RescanDotPhase>;
  /** In-flight document downloads, keyed by `${documentId}:${version}`. */
  downloadingDocIds?: Set<string>;
  /** Called with files dropped onto the panel, to upload them into the current
   *  case. When omitted, drag & drop is disabled. */
  onFilesDropped?: (files: File[]) => void;
}

type StatusFilter = 'all' | DocumentStatus;

export type RescanDotPhase = 'spin' | 'check' | 'x';

interface DocumentTreeNodeProps {
  document: CaseDocument;
  depth: number;
  selectedDocumentId: string | null;
  onSelectDocument: (documentId: string) => void;
  expandedNodes: Set<string>;
  onToggleExpand: (documentId: string) => void;
  formatDate: (dateString: string) => string;
  formatFileSize: (bytes: number) => string;
  onDownload: (documentId: string, version: 'original' | 'redacted') => void;
  onOpenFileDict?: (documentId: string) => void;
  onDeleteDocument?: (documentId: string) => void;
  onReprocessDocument?: (documentId: string) => void;
  onRetryProcessing?: (documentId: string) => void;
  entityProgress?: Map<string, EntityProgressInfo>;
  processingDocumentIds?: Set<string>;
  processingProgress?: Map<string, number>;
  processingEta?: Map<string, number>;
  failedDocumentIds?: Set<string>;
  failureReasons?: Map<string, string>;
  rescanDotStatus?: Map<string, RescanDotPhase>;
  downloadingDocIds?: Set<string>;
}

/**
 * Recursive tree node component for displaying documents with children.
 */
function DocumentTreeNode({
  document,
  depth,
  selectedDocumentId,
  onSelectDocument,
  expandedNodes,
  onToggleExpand,
  formatDate,
  formatFileSize,
  onDownload,
  onOpenFileDict,
  onDeleteDocument,
  onReprocessDocument,
  onRetryProcessing,
  entityProgress,
  processingDocumentIds,
  processingProgress,
  processingEta,
  failedDocumentIds,
  failureReasons,
  rescanDotStatus,
  downloadingDocIds,
}: DocumentTreeNodeProps) {
  const hasChildren = document.children && document.children.length > 0;
  const isExpanded = expandedNodes.has(document.id);
  const isSelected = selectedDocumentId === document.id;
  const isContainer = document.is_container;
  // A container whose contents haven't been extracted yet: the worker ingest job
  // unpacks PST/ZIP children after upload, so for a beat the container exists with
  // zero children. Surface that instead of a silent, empty, "unprocessed" row.
  const isExtractingChildren = isContainer && !hasChildren && (document.children_count ?? 0) === 0;

  const handleChevronClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleExpand(document.id);
  };

  const handleDocumentClick = () => {
    onSelectDocument(document.id);
    // Also toggle expand/collapse if this is a container
    if (hasChildren) {
      onToggleExpand(document.id);
    }
  };

  const handleDocumentKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleDocumentClick();
    }
  };

  const statusColor = DOCUMENT_STATUS_CONFIG[document.status].color;
  const statusLabel = DOCUMENT_STATUS_CONFIG[document.status].label;

  // One tidy, scannable metadata line instead of a wrapping middot chain.
  const metaParts = [getFileType(document.name), formatFileSize(document.file_size)];
  if (document.page_count && !isContainer) metaParts.push(`${document.page_count} pg`);
  if (hasChildren) metaParts.push(`${document.children!.length} items`);
  metaParts.push(formatDate(document.upload_date));

  return (
    <div>
      <div
        onClick={handleDocumentClick}
        onKeyDown={handleDocumentKeyDown}
        role="button"
        tabIndex={0}
        aria-pressed={isSelected}
        className={cn(
          'group relative w-full text-left py-2.5 transition-colors border-l-2',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7035C4] focus-visible:ring-inset',
          isSelected
            ? 'bg-[#faf7fe] border-l-[#C7AFE9]'
            : 'border-l-transparent hover:bg-gray-50'
        )}
        style={{ paddingLeft: `${12 + depth * 16}px`, paddingRight: '12px' }}
      >
        <div className="flex items-start gap-1.5">
          {/* Expand/collapse chevron */}
          {hasChildren ? (
            <IconTooltip label={isExpanded ? 'Collapse' : 'Expand'}>
              <button
                onClick={handleChevronClick}
                className="flex-shrink-0 mt-0.5 p-0.5 hover:bg-gray-200 rounded transition-colors"
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
              >
                <ChevronDown
                  className={cn(
                    'h-3.5 w-3.5 text-gray-500 transition-transform',
                    !isExpanded && '-rotate-90'
                  )}
                />
              </button>
            </IconTooltip>
          ) : null}

          {/* Document info — clear vertical hierarchy: name → meta → status → progress */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium leading-snug truncate" title={document.name}>
              {document.name}
            </p>

            {/* Compact metadata: a single non-wrapping line */}
            <p className="mt-0.5 text-xs text-gray-500 truncate">
              {metaParts.join(' · ')}
            </p>

            {/* Status badge — the triage signal, given its own line */}
            <div className="mt-1.5">
              {isExtractingChildren ? (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium leading-none text-[#7035C4] bg-[#7035C4]/10">
                  <Loader2 className="h-3 w-3 animate-spin flex-shrink-0" />
                  Extracting contents…
                </span>
              ) : (
                <span
                  className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium leading-none"
                  style={{ color: statusColor, backgroundColor: `${statusColor}1a` }}
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: statusColor }}
                  />
                  {statusLabel}
                </span>
              )}
            </div>

            {/* Entity progress bar or processing indicator */}
            {(() => {
              // A rescanning doc stays in `processingDocumentIds`, but we
              // deliberately keep its entity progress bar (showing the current
              // counts) rather than the "Analyzing…" bar — the rescan surfaces
              // only as the dot inline on the label row directly above the bar
              // (see below), and the bar refreshes in place once the rescan
              // lands. The detailed bar stays reserved for genuine initial scans.
              const rescanPhase = rescanDotStatus?.get(document.id);
              const isProcessing = processingDocumentIds?.has(document.id);
              if (isProcessing && !rescanPhase) {
                const analyzeProgress = processingProgress?.get(document.id) ?? 0;
                return (
                  <AnalyzingIndicator
                    progress={analyzeProgress}
                    etaSeconds={processingEta?.get(document.id)}
                  />
                );
              }
              // A failed latest job takes precedence over any (possibly stale or
              // zero) progress counts — surface the failure rather than a
              // misleading "0/0".
              if (failedDocumentIds?.has(document.id)) {
                const reason = failureReasons?.get(document.id);
                return (
                  <div className="mt-2 text-[11px] text-red-600">
                    <div className="flex items-center gap-1">
                      <AlertCircle className="h-3 w-3 flex-shrink-0" />
                      <span>Processing failed</span>
                    </div>
                    {reason && (
                      // Full reason on hover; clamped inline so a long message
                      // doesn't blow out the row.
                      <p className="mt-0.5 text-[10px] leading-snug text-red-500 line-clamp-2" title={reason}>
                        {reason}
                      </p>
                    )}
                  </div>
                );
              }
              const progress = entityProgress?.get(document.id);
              // No progress entry at all means the doc hasn't been processed yet —
              // leave only the status badge. A processed doc that detected nothing
              // still has an entry (detected === 0); show "0/0" rather than hiding it.
              if (!progress) return null;
              const worked = progress.approved + progress.rejected;
              const pct = progress.detected > 0 ? Math.round((worked / progress.detected) * 100) : 100;
              const approvedPct = progress.detected > 0 ? (progress.approved / progress.detected) * 100 : 0;
              const rejectedPct = progress.detected > 0 ? (progress.rejected / progress.detected) * 100 : 0;
              return (
                <div className="mt-1">
                  <div className="flex items-center justify-between text-[10px] text-gray-500 mb-0.5">
                    <span>{worked}/{progress.detected} entities</span>
                    {/* While a rescan is in flight the % is stale, so show the
                        rescan dot here — directly above the bar, on the right —
                        instead. It swaps back to the % once the rescan lands. */}
                    {rescanPhase ? <RescanDot phase={rescanPhase} /> : <span>{pct}%</span>}
                  </div>
                  <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden flex">
                    {/* Nothing detected: a full green bar reads as "processed, clean". */}
                    {progress.detected === 0 && (
                      <div className="h-full w-full bg-green-500" />
                    )}
                    {approvedPct > 0 && (
                      <div
                        className="h-full bg-green-500 transition-all duration-300"
                        style={{ width: `${approvedPct}%` }}
                      />
                    )}
                    {rejectedPct > 0 && (
                      <div
                        className="h-full bg-red-400 transition-all duration-300"
                        style={{ width: `${rejectedPct}%` }}
                      />
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Row actions — kept out of the way; revealed on hover / focus (and on the
            open document) so the card stays calm and the content reads cleanly. */}
        <div
          className={cn(
            'absolute top-2 right-2 flex items-center gap-0.5 rounded-md px-0.5 py-0.5',
            'bg-white/95 shadow-sm ring-1 ring-black/5 backdrop-blur-sm',
            'opacity-0 pointer-events-none transition-opacity duration-150 ease-out',
            'group-hover:opacity-100 group-hover:pointer-events-auto',
            'group-focus-within:opacity-100 group-focus-within:pointer-events-auto'
          )}
        >
          {/* Retry processing — re-queues a plain detection job for a failed
              document. Distinct from the Sparkles reprocess (which steers on
              manual flags); this just tries the original processing again. */}
          {!isContainer && onRetryProcessing && failedDocumentIds?.has(document.id) && (
            <IconTooltip label="Retry processing">
              <button
                onClick={(e) => { e.stopPropagation(); onRetryProcessing(document.id); }}
                disabled={processingDocumentIds?.has(document.id)}
                className={cn(
                  'p-1 rounded transition-colors text-red-500 hover:bg-red-50',
                  'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent'
                )}
                aria-label="Retry processing"
              >
                <RotateCcw className={cn('h-3.5 w-3.5', processingDocumentIds?.has(document.id) && 'animate-spin')} />
              </button>
            </IconTooltip>
          )}
          {/* Reprocess with manual flags — re-runs detection steered by the
              entities the reviewer manually flagged on this document. Leaf docs
              only (containers reprocess their children individually). */}
          {!isContainer && onReprocessDocument && document.status !== 'completed' && (
            <IconTooltip label="Re-run detection using the entities you manually flagged on this document">
              <button
                onClick={(e) => { e.stopPropagation(); onReprocessDocument(document.id); }}
                disabled={processingDocumentIds?.has(document.id)}
                className={cn(
                  'p-1 rounded transition-colors text-[#7035C4] hover:bg-[#f9f5ff]',
                  'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent'
                )}
                aria-label="Reprocess with manual flags"
              >
                <Sparkles className={cn('h-3.5 w-3.5', processingDocumentIds?.has(document.id) && 'animate-pulse')} />
              </button>
            </IconTooltip>
          )}
          {!isContainer && onOpenFileDict && (
            <IconTooltip label="File dictionary">
              <button
                onClick={(e) => { e.stopPropagation(); onOpenFileDict(document.id); }}
                className="p-1 hover:bg-gray-100 rounded transition-colors text-gray-400 hover:text-[#7035C4]"
                aria-label="Open file dictionary"
              >
                <BookOpen className="h-3.5 w-3.5" />
              </button>
            </IconTooltip>
          )}
          <IconTooltip label="Download original">
            <button
              onClick={(e) => { e.stopPropagation(); onDownload(document.id, 'original'); }}
              disabled={downloadingDocIds?.has(`${document.id}:original`)}
              className="p-1 hover:bg-gray-100 rounded transition-colors text-gray-400 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              aria-label="Download original"
            >
              {downloadingDocIds?.has(`${document.id}:original`) ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Download className="h-3.5 w-3.5" />
              )}
            </button>
          </IconTooltip>
          {document.status === 'completed' && (
            <IconTooltip label="Download redacted">
              <button
                onClick={(e) => { e.stopPropagation(); onDownload(document.id, 'redacted'); }}
                disabled={downloadingDocIds?.has(`${document.id}:redacted`)}
                className="p-1 hover:bg-gray-100 rounded transition-colors text-gray-400 hover:text-[#7035C4] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                aria-label="Download redacted"
              >
                {downloadingDocIds?.has(`${document.id}:redacted`) ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <FileCheck className="h-3.5 w-3.5" />
                )}
              </button>
            </IconTooltip>
          )}
          {onDeleteDocument && (
            <IconTooltip label="Delete file">
              <button
                onClick={(e) => { e.stopPropagation(); onDeleteDocument(document.id); }}
                className="p-1 hover:bg-red-50 rounded transition-colors text-gray-400 hover:text-red-600"
                aria-label="Delete file"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </IconTooltip>
          )}
        </div>
      </div>

      {/* Recursively render children */}
      {hasChildren && isExpanded && (
        <div>
          {document.children!.map((child) => (
            <DocumentTreeNode
              key={child.id}
              document={child}
              depth={depth + 1}
              selectedDocumentId={selectedDocumentId}
              onSelectDocument={onSelectDocument}
              expandedNodes={expandedNodes}
              onToggleExpand={onToggleExpand}
              formatDate={formatDate}
              formatFileSize={formatFileSize}
              onDownload={onDownload}
              onOpenFileDict={onOpenFileDict}
              onDeleteDocument={onDeleteDocument}
              onReprocessDocument={onReprocessDocument}
              onRetryProcessing={onRetryProcessing}
              entityProgress={entityProgress}
              processingDocumentIds={processingDocumentIds}
              processingProgress={processingProgress}
              processingEta={processingEta}
              failedDocumentIds={failedDocumentIds}
              failureReasons={failureReasons}
              rescanDotStatus={rescanDotStatus}
              downloadingDocIds={downloadingDocIds}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Build a tree structure from flat documents list using parent_id.
 * Returns only root documents (those without a parent_id).
 */
function buildDocumentTree(documents: CaseDocument[]): CaseDocument[] {
  // Create a map of document ID to document (with empty children array)
  const docMap = new Map<string, CaseDocument>();
  documents.forEach((doc) => {
    docMap.set(doc.id, { ...doc, children: [] });
  });

  // Build tree by assigning children to parents
  const rootDocs: CaseDocument[] = [];
  documents.forEach((doc) => {
    const docWithChildren = docMap.get(doc.id)!;
    if (doc.parent_id && docMap.has(doc.parent_id)) {
      const parent = docMap.get(doc.parent_id)!;
      parent.children = parent.children || [];
      parent.children.push(docWithChildren);
    } else {
      // No parent or parent not in list - treat as root
      rootDocs.push(docWithChildren);
    }
  });

  return rootDocs;
}

/**
 * Filter document tree, keeping nodes that match filter or have matching descendants.
 * Also keeps ancestors of matching nodes.
 */
function filterDocumentTree(
  documents: CaseDocument[],
  statusFilter: StatusFilter
): CaseDocument[] {
  if (statusFilter === 'all') return documents;

  function filterNode(doc: CaseDocument): CaseDocument | null {
    // Filter children recursively
    const filteredChildren = (doc.children || [])
      .map(filterNode)
      .filter((c): c is CaseDocument => c !== null);

    // Include this node if it matches OR has matching descendants
    if (doc.status === statusFilter || filteredChildren.length > 0) {
      return { ...doc, children: filteredChildren };
    }

    return null;
  }

  return documents.map(filterNode).filter((d): d is CaseDocument => d !== null);
}

/**
 * Count total documents in tree (including nested children).
 */
function countDocuments(documents: CaseDocument[]): number {
  let count = 0;
  function countNode(doc: CaseDocument) {
    count++;
    (doc.children || []).forEach(countNode);
  }
  documents.forEach(countNode);
  return count;
}

export function DocumentsPanel({
  documents,
  selectedDocumentId,
  onSelectDocument,
  isCollapsed,
  onToggleCollapse,
  onDownload,
  onOpenFileDict,
  onDeleteDocument,
  onReprocessDocument,
  onRetryProcessing,
  entityProgress,
  processingDocumentIds,
  processingProgress,
  processingEta,
  failedDocumentIds,
  failureReasons,
  rescanDotStatus,
  downloadingDocIds,
  onFilesDropped,
}: DocumentsPanelProps) {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  // Drag & drop upload into the current case. `noClick`/`noKeyboard` keep the
  // panel's rows fully interactive (clicking a document must not open a file
  // dialog) — this reuses the same upload path as the create-case wizard via the
  // parent's onFilesDropped handler. Disabled entirely when no handler is wired.
  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) onFilesDropped?.(acceptedFiles);
    },
    [onFilesDropped]
  );
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: UPLOAD_ACCEPTED_FILE_TYPES,
    maxSize: UPLOAD_MAX_FILE_SIZE,
    multiple: true,
    noClick: true,
    noKeyboard: true,
    disabled: !onFilesDropped,
  });

  // Build tree structure from flat documents
  const documentTree = useMemo(() => buildDocumentTree(documents), [documents]);

  // Apply status filter to tree
  const filteredTree = useMemo(
    () => filterDocumentTree(documentTree, statusFilter),
    [documentTree, statusFilter]
  );

  const totalCount = useMemo(() => countDocuments(documentTree), [documentTree]);

  const onToggleExpand = useCallback((documentId: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(documentId)) {
        next.delete(documentId);
      } else {
        next.add(documentId);
      }
      return next;
    });
  }, []);

  // Collapsed state - just show toggle button
  if (isCollapsed) {
    return (
      <div className="w-10 border-r border-border bg-gray-50 flex flex-col items-center py-4">
        <IconTooltip label="Expand documents panel">
          <button
            onClick={onToggleCollapse}
            className="p-1.5 hover:bg-gray-200 rounded-md transition-colors"
            aria-label="Expand documents panel"
          >
            <ChevronRight className="h-5 w-5 text-gray-600" />
          </button>
        </IconTooltip>
        <div className="mt-4 flex flex-col items-center gap-2">
          <FileText className="h-5 w-5 text-gray-400" />
          <span className="text-xs text-gray-500 writing-vertical-lr text-center">
            {totalCount} docs
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      {...getRootProps()}
      className="relative w-64 border-r border-border bg-white flex flex-col h-full overflow-hidden"
    >
      {/* Hidden input backing the dropzone (click is disabled; drag-only) */}
      <input {...getInputProps()} />

      {/* Drag feedback overlay — covers the panel while files are being dragged over it */}
      {isDragActive && (
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 bg-[#f3eafc]/95 border-2 border-dashed border-[#7035C4] pointer-events-none text-center px-4">
          <UploadCloud className="h-8 w-8 text-[#7035C4]" />
          <p className="text-sm font-semibold text-[#7035C4]">Drop files to upload</p>
          <p className="text-xs text-gray-600">They'll be added to this case</p>
        </div>
      )}

      {/* Header */}
      <div className="p-3 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="font-medium text-sm">Documents</h3>
          <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
            {totalCount}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <IconTooltip label="Collapse documents panel">
            <button
              onClick={onToggleCollapse}
              className="p-1 hover:bg-gray-100 rounded-md transition-colors"
              aria-label="Collapse documents panel"
            >
              <ChevronLeft className="h-4 w-4 text-gray-600" />
            </button>
          </IconTooltip>
        </div>
      </div>

      {/* Filter */}
      <div className="p-3 border-b border-border">
        <Select
          value={statusFilter}
          onValueChange={(value: string) => setStatusFilter(value as StatusFilter)}
        >
          <SelectTrigger size="sm">
            <SelectValue placeholder="All Stages" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stages</SelectItem>
            {(Object.keys(DOCUMENT_STATUS_CONFIG) as DocumentStatus[]).map((status) => (
              <SelectItem key={status} value={status}>
                <div className="flex items-center gap-2">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: DOCUMENT_STATUS_CONFIG[status].color }}
                  />
                  {DOCUMENT_STATUS_CONFIG[status].label}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Document Tree */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {filteredTree.length === 0 ? (
          <div className="p-4 text-center text-sm text-gray-500">
            {totalCount === 0 ? 'No documents yet' : 'No documents match filter'}
          </div>
        ) : (
          <div className="pb-1">
            {filteredTree.map((doc) => (
              <DocumentTreeNode
                key={doc.id}
                document={doc}
                depth={0}
                selectedDocumentId={selectedDocumentId}
                onSelectDocument={onSelectDocument}
                expandedNodes={expandedNodes}
                onToggleExpand={onToggleExpand}
                formatDate={formatDate}
                formatFileSize={formatFileSize}
                onDownload={onDownload}
                onOpenFileDict={onOpenFileDict}
                onDeleteDocument={onDeleteDocument}
                onReprocessDocument={onReprocessDocument}
                onRetryProcessing={onRetryProcessing}
                entityProgress={entityProgress}
                processingDocumentIds={processingDocumentIds}
                processingProgress={processingProgress}
                processingEta={processingEta}
                failedDocumentIds={failedDocumentIds}
                failureReasons={failureReasons}
                rescanDotStatus={rescanDotStatus}
                downloadingDocIds={downloadingDocIds}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
